/**
 * "Anpassa text" (ADR-0102 phase 1) — its own screen per beta contract:
 * the full-width document with the "Ändringar" side panel. Reached from
 * the beta editor's Avtal tab; editable for the author in draft and
 * pending, read-only otherwise.
 */

import { useCallback } from 'react';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { hasComposition } from '@hyranai/regta-blocks';
import { ApiError, tenantApi, type ContractDetail } from '../../../api.js';
import { useTenantSession } from '../../../auth-context.js';
import { useInvalidate, useTenantQuery } from '../../../query.js';
import { usePageTitle } from '../../../use-page-title.js';
import { StatusChip } from '../../../components/ContractStatusBar.js';
import { STALE_APPROVAL_NOTICE, approvalIsStale, useApprovalResetGuard } from '../../../lib/approval-reset.js';
import { AdaptTextPanel } from '../finetune/AdaptTextPanel.js';
import { adaptWindow } from '../finetune/model.js';
import { EDITOR_AVTAL_WIDTH } from '../../../lib/editor-layout.js';
import { useFieldActions } from '../../../components/beta-review/FieldActions.js';
import { fieldHref, reviewHref } from '../../../components/beta-review/review-model.js';

export function AdaptTextPage() {
  const { id } = useParams<{ id: string }>();
  const { actor } = useTenantSession();
  const invalidate = useInvalidate();

  const contractQ = useTenantQuery(
    'contracts.get',
    (cid) => tenantApi.contracts.get(cid as string),
    id ? [id] : [],
    { ignoreActiveGroup: true, revalidateOnFocus: false },
  );
  const reviewsQ = useTenantQuery(
    'reviews.list_for_contract',
    (cid) => tenantApi.reviews.list_for_contract({ contract_id: cid as string }),
    id ? [id] : [],
    { ignoreActiveGroup: true, revalidateOnFocus: false },
  );
  // ADR-0102 addendum 3: each template bilaga is its own document with its own log.
  const attachmentsQ = useTenantQuery(
    'contract_attachments.list',
    (cid) => tenantApi.contract_attachments.list({ contract_id: cid as string }),
    id ? [id] : [],
    { ignoreActiveGroup: true, revalidateOnFocus: false },
  );
  const [search, setSearch] = useSearchParams();
  const bilagor = (attachmentsQ.data?.attachments ?? []).filter(
    (a) => a.kind === 'template' && a.templateKey && a.templateVersion && hasComposition(a.templateKey, a.templateVersion),
  );
  const requestedDoc = search.get('dokument');
  const documentScope = bilagor.some((a) => a.id === requestedDoc) ? requestedDoc : null;
  const contract: ContractDetail | null = contractQ.data?.contract ?? null;
  const reviews = reviewsQ.data?.reviews ?? [];
  const approval = contract?.approval ?? reviewsQ.data?.approval ?? null;
  const guard = useApprovalResetGuard(approval);
  const navigate = useNavigate();
  // Field navigation (ADR-0102 addendum 2): a yellow value → "Ändra i formuläret" / "Kommentera".
  const fieldActions = useFieldActions({
    contract,
    onOpenField: (t) => contract && navigate(fieldHref(contract.id, t.key, t.attachmentId)),
  });

  usePageTitle(contract ? `Anpassa text – ${contract.title || 'Avtal'}` : null);

  const onChanged = useCallback(() => {
    if (!contract) return;
    void Promise.all([
      invalidate('contracts.get', contract.id),
      invalidate('reviews.list_for_contract', contract.id),
      invalidate('contracts.list'),
      // Own fields defined here show in the beta form ("Egna uppgifter").
      invalidate('contracts.finetune_get', contract.id),
      invalidate('contract_attachments.list', contract.id),
    ]);
  }, [contract, invalidate]);

  if (contractQ.error instanceof ApiError && contractQ.error.status === 404) {
    return (
      <div className="rounded border border-stone-200 bg-white p-4 text-sm text-stone-600">
        Avtalet finns inte längre.{' '}
        <Link to="/contracts" className="font-medium text-stone-900 underline">
          Till avtalslistan
        </Link>
      </div>
    );
  }
  if (contractQ.error && !contract) {
    return (
      <div className="rounded border border-red-300 bg-red-50 p-3 text-sm text-red-700">
        {(contractQ.error as Error).message}
      </div>
    );
  }
  if (!contract) return <div className="text-sm text-stone-400">Laddar…</div>;
  if (contract.documentEngine !== 'ir') return <Navigate to={`/contracts/${contract.id}/edit`} replace />;

  const editWindow = adaptWindow(contract.status);
  const lastReviewApproved = reviews.find((r) => r.status !== 'pending')?.status === 'approved';
  // Only the status changes what this screen may do; text edits refresh the panel themselves.
  const refreshKey = contract.status;

  return (
    <div className={EDITOR_AVTAL_WIDTH + ' flex h-full flex-col'}>
      <div className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-1">
        <Link
          to={`/contracts/${contract.id}/beta`}
          className="inline-flex items-center gap-1 text-xs text-stone-500 transition-colors hover:text-stone-800 hover:underline"
        >
          ← Tillbaka till avtalet
        </Link>
      </div>
      {fieldActions.element}
      {fieldActions.error && (
        <p role="alert" className="mb-3 rounded border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-800">
          {fieldActions.error}{' '}
          <button type="button" className="underline" onClick={fieldActions.clearError}>
            Stäng
          </button>
        </p>
      )}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <h1 className="text-lg font-semibold text-stone-900">Anpassa text</h1>
        <span className="text-sm text-stone-500">· {contract.title || 'Avtal'}</span>
        <StatusChip status={contract.status} reviewed={contract.status === 'pending' && lastReviewApproved} />
        {contract.status === 'in_review' && (
          <Link to={reviewHref(contract.id, 'text', documentScope)} className="ml-auto text-xs font-medium underline">
            Till granskningen
          </Link>
        )}
      </div>

      {editWindow.allowEdit && approval?.approvalWouldReset && (
        <p className="mb-3 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          Avtalet är godkänt i granskning. Ändrar du texten återställs godkännandet och avtalet behöver granskas igen.
        </p>
      )}
      {approvalIsStale(reviews) && (
        <p className="mb-3 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          {STALE_APPROVAL_NOTICE}
        </p>
      )}
      {editWindow.allowEdit && approval?.requireReviewForDeviatingDocuments && (
        <p className="mb-3 rounded border border-stone-200 bg-stone-50 px-3 py-2 text-xs text-stone-700">
          Avtal med anpassad text måste granskas och godkännas innan de kan skickas för signering.
        </p>
      )}

      {bilagor.length > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2 text-xs" role="tablist" aria-label="Dokument">
          <span className="text-stone-500">Dokument:</span>
          {[{ id: null as string | null, label: 'Huvudavtal' }, ...bilagor.map((a) => ({ id: a.id as string | null, label: `${a.number != null ? `Bilaga ${a.number}` : 'Bilaga'} – ${a.displayName}` }))].map((d) => (
            <button
              key={d.id ?? 'main'}
              type="button"
              role="tab"
              aria-selected={documentScope === d.id}
              onClick={() =>
                setSearch(
                  (prev) => {
                    const next = new URLSearchParams(prev);
                    if (d.id) next.set('dokument', d.id);
                    else next.delete('dokument');
                    return next;
                  },
                  { replace: true },
                )
              }
              className={
                'rounded border px-2 py-1 transition-colors ' +
                (documentScope === d.id
                  ? 'border-stone-800 bg-stone-800 text-white'
                  : 'border-stone-300 bg-white text-stone-700 hover:bg-stone-50')
              }
            >
              {d.label}
            </button>
          ))}
        </div>
      )}

      <main className="flex min-h-[75vh] flex-1 flex-col overflow-hidden border border-stone-200 bg-white">
        <AdaptTextPanel
          key={documentScope ?? 'main'}
          contractId={contract.id}
          attachmentId={documentScope}
          refreshKey={refreshKey}
          actorId={actor?.id ?? null}
          allowEdit={editWindow.allowEdit}
          readOnlyText={editWindow.readOnlyText}
          beforeChange={guard}
          onChanged={onChanged}
          onValueClick={(key, rect) => fieldActions.open({ key, attachmentId: documentScope }, rect)}
        />
      </main>
    </div>
  );
}
